import type {
  ResourcePointDeleteResult,
  ResourcePointListPage,
  ResourcePointListParams,
  ResourcePointMediaItem,
  ResourcePointMediaMutation,
  ResourcePointSkuItem,
  ResourcePointSkuMutation,
  ResourcePointSpuItem,
  ResourcePointSpuMutation,
} from "../types";

export type ResourcePointSpuItemRaw = {
  vector_sync_warning?: string | null;
  resource_point_spu_id: string;
  legacy_id: string | null;
  point_type: string;
  point_sub_type: string | null;
  point_name: string;
  point_short_name: string | null;
  head_image_url: string | null;
  country: string;
  province: string | null;
  city: string | null;
  district: string | null;
  address: string | null;
  lng: number | string | null;
  lat: number | string | null;
  geo_status: "unverified" | "confirmed";
  visit_mode: string | null;
  default_visit_duration: number | null;
  suitable_majors: string | null;
  suitable_major_scope: string;
  teaching_tags: string | null;
  is_ai_created: boolean;
  itinerary_count: number;
  order_itinerary_count: number;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
  is_disabled: boolean;
  status: string;
  remark: string;
  sku_count: number;
  teaching_media_count?: number;
  product_media_count?: number;
};

export type ResourcePointSkuItemRaw = {
  resource_point_sku_id: string;
  resource_point_spu_id: string;
  sku_name: string;
  ticket_resource_type: string;
  traveler_role: string;
  settlement_unit: string;
  reservation_required: boolean;
  reservation_info_markdown: string | null;
  min_group_size: number | null;
  max_group_size: number | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
  is_disabled: boolean;
  status: string;
  remark: string;
};

export type ResourcePointListPageRaw = {
  items: ResourcePointSpuItemRaw[];
  total: number;
  page: number;
  page_size: number;
};

export type ResourcePointDeleteResultRaw = {
  id: string;
  disabled_sku_count: number;
};

export type ResourcePointMediaItemRaw = {
  resource_point_media_id: string;
  resource_point_spu_id: string;
  media_scene: string;
  media_name: string;
  file_url: string;
  file_ext: string | null;
  content_text: string | null;
  sort_no: number;
  media_status: string;
  created_at: string;
  updated_at: string;
  remark: string;
};

export type ResourcePointMediaMutationRaw = {
  media_scene: string;
  media_name: string;
  file_url: string;
  file_ext: string | null;
  content_text: string | null;
  sort_no: number;
  remark: string;
};

export type ResourcePointSpuMutationRaw = {
  point_type: string;
  point_sub_type: string | null;
  point_name: string;
  point_short_name: string | null;
  head_image_url: string | null;
  country: string;
  province: string | null;
  city: string | null;
  district: string | null;
  address: string | null;
  lng: number | null;
  lat: number | null;
  geo_status: "unverified" | "confirmed";
  visit_mode: string | null;
  teaching_tags: string | null;
  is_disabled: boolean;
  status: string;
  remark: string;
};

export type ResourcePointSkuMutationRaw = {
  sku_name: string;
  ticket_resource_type: string;
  traveler_role: string;
  settlement_unit: string;
  reservation_required: boolean;
  reservation_info_markdown: string | null;
  min_group_size: number | null;
  max_group_size: number | null;
  is_disabled: boolean;
  status: string;
  remark: string;
};

/** 将 SPU 列表查询参数映射为后端 snake_case 契约。 */
export function mapResourcePointListQueryParams(params: ResourcePointListParams) {
  return {
    page: params.page,
    page_size: params.pageSize,
    keyword: normalizeOptionalText(params.keyword),
    point_type: normalizeOptionalText(params.pointType),
    point_sub_type: normalizeOptionalText(params.pointSubType),
    country: normalizeOptionalText(params.country),
    province: normalizeOptionalText(params.province),
    city: normalizeOptionalText(params.city),
    district: normalizeOptionalText(params.district),
    status: params.status,
    geo_status: params.geoStatus,
    is_disabled: params.isDisabled,
    sort_field: params.sortField,
    sort_direction: params.sortDirection,
  };
}

/** 将 SPU 新增或编辑值映射为后端 snake_case 载荷。 */
export function mapResourcePointSpuMutationPayload(
  payload: ResourcePointSpuMutation,
): ResourcePointSpuMutationRaw {
  return {
    point_type: payload.pointType.trim(),
    point_sub_type: normalizeNullableText(payload.pointSubType),
    point_name: payload.pointName.trim(),
    point_short_name: normalizeNullableText(payload.pointShortName),
    head_image_url: normalizeNullableText(payload.headImageUrl),
    country: payload.country.trim(),
    province: normalizeNullableText(payload.province),
    city: normalizeNullableText(payload.city),
    district: normalizeNullableText(payload.district),
    address: normalizeNullableText(payload.address),
    lng: payload.lng ?? null,
    lat: payload.lat ?? null,
    geo_status: payload.geoStatus === "confirmed" ? "confirmed" : "unverified",
    visit_mode: normalizeNullableText(payload.visitMode),
    teaching_tags: normalizeNullableText(payload.teachingTags),
    is_disabled: payload.isDisabled,
    status: payload.status,
    remark: payload.remark.trim(),
  };
}

/** 将 SKU 新增或编辑值映射为后端 snake_case 载荷。 */
export function mapResourcePointSkuMutationPayload(
  payload: ResourcePointSkuMutation,
): ResourcePointSkuMutationRaw {
  return {
    sku_name: payload.skuName.trim(),
    ticket_resource_type: payload.ticketResourceType,
    traveler_role: payload.travelerRole,
    settlement_unit: payload.settlementUnit,
    reservation_required: payload.reservationRequired,
    reservation_info_markdown: normalizeNullableText(payload.reservationInfoMarkdown),
    min_group_size: payload.minGroupSize ?? null,
    max_group_size: payload.maxGroupSize ?? null,
    is_disabled: payload.isDisabled,
    status: payload.status,
    remark: payload.remark.trim(),
  };
}

/** 将后端 SPU 列表页映射为前端领域模型。 */
export function mapResourcePointListPage(raw: ResourcePointListPageRaw): ResourcePointListPage {
  return {
    items: raw.items.map(mapResourcePointSpuItem),
    total: raw.total,
    page: raw.page,
    pageSize: raw.page_size,
  };
}

/** 将后端 SPU 单项完整映射为前端领域模型。 */
export function mapResourcePointSpuItem(raw: ResourcePointSpuItemRaw): ResourcePointSpuItem {
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    resourcePointSpuId: raw.resource_point_spu_id,
    legacyId: raw.legacy_id ?? undefined,
    pointType: raw.point_type,
    pointSubType: raw.point_sub_type ?? undefined,
    pointName: raw.point_name,
    pointShortName: raw.point_short_name ?? undefined,
    headImageUrl: raw.head_image_url ?? undefined,
    country: raw.country,
    province: raw.province ?? undefined,
    city: raw.city ?? undefined,
    district: raw.district ?? undefined,
    address: raw.address ?? undefined,
    lng: mapOptionalNumber(raw.lng),
    lat: mapOptionalNumber(raw.lat),
    geoStatus: raw.geo_status === "confirmed" ? "confirmed" : "unverified",
    visitMode: raw.visit_mode ?? undefined,
    defaultVisitDuration: raw.default_visit_duration ?? undefined,
    suitableMajors: raw.suitable_majors ?? undefined,
    suitableMajorScope: raw.suitable_major_scope,
    teachingTags: raw.teaching_tags ?? undefined,
    isAiCreated: raw.is_ai_created,
    itineraryCount: raw.itinerary_count,
    orderItineraryCount: raw.order_itinerary_count,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdBy: raw.created_by,
    updatedBy: raw.updated_by,
    isDisabled: raw.is_disabled,
    status: raw.status,
    remark: raw.remark,
    skuCount: raw.sku_count,
    teachingMediaCount: raw.teaching_media_count ?? 0,
    productMediaCount: raw.product_media_count ?? 0,
  };
}

/** 将后端 SKU 单项完整映射为前端领域模型。 */
export function mapResourcePointSkuItem(raw: ResourcePointSkuItemRaw): ResourcePointSkuItem {
  return {
    resourcePointSkuId: raw.resource_point_sku_id,
    resourcePointSpuId: raw.resource_point_spu_id,
    skuName: raw.sku_name,
    ticketResourceType: raw.ticket_resource_type,
    travelerRole: raw.traveler_role,
    settlementUnit: raw.settlement_unit,
    reservationRequired: raw.reservation_required,
    reservationInfoMarkdown: raw.reservation_info_markdown ?? undefined,
    minGroupSize: raw.min_group_size ?? undefined,
    maxGroupSize: raw.max_group_size ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    createdBy: raw.created_by,
    updatedBy: raw.updated_by,
    isDisabled: raw.is_disabled,
    status: raw.status,
    remark: raw.remark,
  };
}

/** 将后端逻辑删除结果映射为前端领域模型。 */
export function mapResourcePointDeleteResult(
  raw: ResourcePointDeleteResultRaw,
): ResourcePointDeleteResult {
  return {
    id: raw.id,
    disabledSkuCount: raw.disabled_sku_count,
  };
}

/** 将后端考察点媒体映射为前端领域模型。 */
export function mapResourcePointMediaItem(raw: ResourcePointMediaItemRaw): ResourcePointMediaItem {
  return {
    resourcePointMediaId: raw.resource_point_media_id,
    resourcePointSpuId: raw.resource_point_spu_id,
    mediaScene: raw.media_scene,
    mediaName: raw.media_name,
    fileUrl: raw.file_url,
    fileExt: raw.file_ext ?? undefined,
    contentText: raw.content_text ?? undefined,
    sortNo: raw.sort_no,
    mediaStatus: raw.media_status,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark,
  };
}

/** 将前端媒体写入载荷映射为后端契约。 */
export function mapResourcePointMediaMutationPayload(
  payload: ResourcePointMediaMutation,
): ResourcePointMediaMutationRaw {
  return {
    media_scene: payload.mediaScene,
    media_name: payload.mediaName.trim(),
    file_url: payload.fileUrl.trim(),
    file_ext: normalizeNullableText(payload.fileExt),
    content_text: normalizeNullableText(payload.contentText),
    sort_no: payload.sortNo,
    remark: payload.remark.trim(),
  };
}

/** 将可选文本归一为去除首尾空白后的值。 */
function normalizeOptionalText(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

/** 将可选文本归一为后端可空字段。 */
function normalizeNullableText(value: string | undefined): string | null {
  return normalizeOptionalText(value) ?? null;
}

/** 将 Decimal 兼容响应归一为可选数值。 */
function mapOptionalNumber(value: number | string | null): number | undefined {
  return value === null ? undefined : Number(value);
}
